import { Email } from '@/api/submission-scheduler/emails';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ColumnFilterInput } from '@/components/ui/custom/column-filter-input';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { ArrowUpDown } from 'lucide-react';

import { EditEmailDialog } from './edit-dialog';

export const columns: ColumnDef<Email>[] = [
	{
		id: 'select',
		header: ({ table }) => {
			const scheduledRows = table
				.getRowModel()
				.rows.filter((row) => row.original.status.toLowerCase() === 'scheduled');
			const allScheduledSelected =
				scheduledRows.length > 0 &&
				scheduledRows.every((row) => row.getIsSelected());
			const someScheduledSelected = scheduledRows.some((row) =>
				row.getIsSelected(),
			);

			return (
				<div className="mr-1 flex justify-center">
					<Checkbox
						aria-label="Select all"
						checked={
							allScheduledSelected
								? true
								: someScheduledSelected
									? 'indeterminate'
									: false
						}
						onCheckedChange={(value) => {
							scheduledRows.forEach((row) => row.toggleSelected(!!value));
						}}
					/>
				</div>
			);
		},
		cell: ({ row }) => (
			<div className="mr-4 flex justify-center">
				<Checkbox
					aria-label="Select row"
					checked={row.getIsSelected()}
					disabled={row.original.status.toLowerCase() !== 'scheduled'}
					onCheckedChange={(value) => row.toggleSelected(!!value)}
				/>
			</div>
		),
		enableSorting: false,
		enableHiding: false,
	},
	{
		accessorKey: 'sender',
		header: ({ column }) => (
			<div className="mb-4 flex flex-col gap-2">
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					Sender
					<ArrowUpDown />
				</Button>
				<ColumnFilterInput
					column={column}
					placeholder="Type to search sender"
				/>
			</div>
		),
		cell: ({ row }) => <div className="font-medium">{row.original.sender}</div>,
	},
	{
		accessorKey: 'recipient',
		header: ({ column }) => (
			<div className="mb-4 flex flex-col gap-2">
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					Recipient
					<ArrowUpDown />
				</Button>
				<ColumnFilterInput
					column={column}
					placeholder="Type to search recipient"
				/>
			</div>
		),
		cell: ({ row }) => (
			<div className="font-medium">{row.original.recipient}</div>
		),
	},
	{
		accessorKey: 'subject',
		header: ({ column }) => (
			<div className="mb-4 flex flex-col gap-2">
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					Subject
					<ArrowUpDown />
				</Button>
				<ColumnFilterInput
					column={column}
					placeholder="Type to search subject"
				/>
			</div>
		),
		cell: ({ row }) => <div className="font-medium">{row.original.subject}</div>,
	},
	{
		accessorKey: 'deliveryDateTime',
		header: ({ column }) => (
			<div className="mb-4 flex flex-col gap-2">
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					Delivery Date
					<ArrowUpDown />
				</Button>
				<ColumnFilterInput
					column={column}
					type="date"
				/>
			</div>
		),
		cell: ({ row }) => {
			return (
				<div className="font-medium">
					{row.original.deliveryDateTime
						? format(row.original.deliveryDateTime, 'MM/dd/yyyy hh:mm a')
						: '---'}
				</div>
			);
		},
	},
	{
		accessorKey: 'status',
		header: ({ column }) => (
			<div className="mb-4 flex flex-col gap-2">
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					Status
					<ArrowUpDown />
				</Button>
				<ColumnFilterInput
					column={column}
					placeholder="Type to search status"
				/>
			</div>
		),
		cell: ({ row }) => <div className="font-medium">{row.original.status}</div>,
	},
	{
		id: 'actions',
		header: () => <div className="m-auto"></div>,
		cell: ({ row }) => {
			if (row.original.status.toLowerCase() === 'scheduled') {
				return <EditEmailDialog email={row.original} />;
			}
			return null;
		},
	},
];
